// src/pages/Help/EsimGuide.jsx
"use client";

import { useEffect } from "react";

export default function EsimGuide() {
  useEffect(() => {
    const hash = window.location.hash;

    if (hash) {
      const id = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);

      return () => clearTimeout(id);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background text-textPrimary">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-primary">eSIM Guide</h1>
          <p className="mt-2 text-sm text-textSecondary">
            Quick requirements, install steps, and a straight-up compatibility list.
          </p>
        </header>

        {/* IMPORTANT NOTICE */}
        <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 mb-6 text-sm">
          <p className="font-semibold">Important</p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li>There are many devices that support eSIM.</li>
            <li>
              To use a roaming eSIM, your device must be <strong>carrier-unlocked</strong>.
            </li>
            <li>
              On devices with one physical SIM and one eSIM, keep your primary carrier on the physical SIM and install
              the roaming eSIM on the eSIM slot.
            </li>
            <li>Have a strong Wi-Fi or 4G connection during install.</li>
          </ul>
        </div>

        {/* ReloadPI post-purchase shortcuts */}
        <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
          <h3 className="font-medium">After purchase in ReloadPI</h3>
          <p className="mt-1">
            Open <strong>My Transactions</strong> for the order. You’ll see three install options:
          </p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li>
              <strong>Install on iOS (one-tap):</strong> On your iPhone, tap <em>Install on iOS</em> to launch the
              built-in eSIM installer with your SM-DP+ and activation code prefilled. Follow the prompts to finish.
            </li>
            <li>
              <strong>Get QR code:</strong> Tap <em>Get QR</em> to display your eSIM QR. On iPhone Safari, you can{' '}
              <em>long-press</em> the QR and choose <em>Add eSIM</em> to scan and install directly.
            </li>
            <li>
              <strong>Full LPA payload (copy-paste):</strong> We show the complete <em>LPA</em> string (e.g.{' '}
              <code>LPA:1$&lt;SM-DP+&gt;$&lt;ActivationCode&gt;</code>) so you can choose <em>Enter Details Manually</em>{' '}
              on iOS/Android and paste it in.
            </li>
          </ul>
          <p className="mt-2 text-xs text-textSecondary">
            QR codes are often one-time use — don’t delete the profile after activation. Availability of these options
            can vary by device/region/firmware.
          </p>
        </div>

        {/* REQUIREMENTS */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold">Requirements</h2>
          <ul className="list-disc ml-5 mt-2 text-sm space-y-1">
            <li>Stable Wi-Fi or 4G connection</li>
            <li>
              eSIM-compatible device (dial <code>*#06#</code> — if an <strong>EID</strong> shows, your device supports
              eSIM)
            </li>
            <li>
              Device is <strong>not</strong> locked to a specific carrier
            </li>
          </ul>
        </section>

        {/* HOW TO CHECK (iOS / Android) */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold">Check if your device is unlocked & supports eSIM</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div className="rounded-lg border border-border p-4">
              <h3 className="font-medium">Apple iOS (iPhone/iPad)</h3>
              <ol className="list-decimal ml-5 mt-2 text-sm space-y-1">
                <li>Settings → General → About</li>
                <li>
                  Under <em>Carrier Lock</em>, look for <strong>“No SIM restrictions”</strong> (unlocked)
                </li>
                <li>
                  Under <em>Available SIM</em> (iOS 15+) or <em>Digital SIM</em> (earlier), check for a 15-digit IMEI
                  (eSIM support)
                </li>
              </ol>
            </div>
            <div className="rounded-lg border border-border p-4">
              <h3 className="font-medium">Android (general)</h3>
              <ol className="list-decimal ml-5 mt-2 text-sm space-y-1">
                <li>Settings → Network & Internet (or Connections)</li>
                <li>
                  Look for <em>eSIM</em> or <em>Add eSIM</em> options
                </li>
                <li>If not present, your model/region/firmware may not support eSIM</li>
              </ol>
            </div>
          </div>
        </section>

        {/* INSTALL STEPS */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold">Install eSIM</h2>
          <div className="rounded-lg border border-border p-4 mt-2 text-sm">
            <h3 className="font-medium">Via QR Code (iOS/Android)</h3>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Ensure strong Wi-Fi</li>
              <li>Open the eSIM menu (iOS: Settings → Cellular → Add eSIM)</li>
              <li>Scan the QR code provided after purchase</li>
              <li>Follow on-screen prompts until the profile is active</li>
            </ol>
            <h3 className="font-medium mt-4">Manual Install (SM-DP+ / Activation Code)</h3>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>Choose “Enter Details Manually”</li>
              <li>Enter SM-DP+ address and Activation Code provided with your order</li>
            </ul>
            <p className="mt-3 text-xs text-textSecondary">
              Notes: QR codes are often one-time use. Don’t delete the eSIM after activation. Some devices limit active
              eSIMs by region/firmware. Most data eSIMs don’t include voice/SMS unless stated.
            </p>
          </div>
        </section>

        {/* COMPATIBILITY LIST */}
        <section className="mb-12" id="compatibility">
          <h2 className="text-xl font-semibold">Compatibility List</h2>

          {/* Apple */}
          <div className="mt-3 rounded-lg border border-border p-4">
            <h3 className="font-medium">Apple iOS</h3>
            <ul className="list-disc ml-5 mt-2 text-sm grid grid-cols-1 sm:grid-cols-2 gap-y-1">
              {[
                'iPhone XR',
                'iPhone XS',
                'iPhone XS Max',
                'iPhone 11',
                'iPhone 11 Pro',
                'iPhone 11 Pro Max',
                'iPhone SE 2 (2020)',
                'iPhone 12',
                'iPhone 12 Mini',
                'iPhone 12 Pro',
                'iPhone 12 Pro Max',
                'iPhone 13',
                'iPhone 13 Mini',
                'iPhone 13 Pro',
                'iPhone 13 Pro Max',
                'iPhone SE 3 (2022)',
                'iPhone 14',
                'iPhone 14 Plus',
                'iPhone 14 Pro',
                'iPhone 14 Pro Max',
                'iPhone 15',
                'iPhone 15 Plus',
                'iPhone 15 Pro',
                'iPhone 15 Pro Max',
                'iPad Pro 11″ (A2068, since 2020)',
                'iPad Pro 12.9″ (A2069, since 2020)',
                'iPad Air (A2123, since 2019)',
                'iPad (A2198, since 2019)',
                'iPad Mini (A2124, since 2019)',
              ].map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
            <div className="mt-3 text-xs text-textSecondary space-y-1">
              <p>* iPhone 13/14/15 can have two eSIMs activated at the same time.</p>
              <p>
                * Mainland China iPhones and many Hong Kong/Macao models (except 13 mini, 12 mini, SE 2020, XS) do not
                support eSIM.
              </p>
              <p>* In the USA, iPhone 14/14 Plus/14 Pro/14 Pro Max are eSIM-only (no physical SIM).</p>
            </div>
          </div>

          {/* Android */}
          <div className="mt-4 rounded-lg border border-border p-4">
            <h3 className="font-medium">Android</h3>

            {/* Samsung */}
            <div className="mt-2">
              <p className="font-medium text-sm">Samsung (compatible)</p>
              <ul className="list-disc ml-5 mt-1 text-sm grid grid-cols-1 sm:grid-cols-2 gap-y-1">
                {[
                  'Galaxy S20',
                  'Galaxy S20+',
                  'Galaxy S20 Ultra',
                  'Galaxy S21',
                  'Galaxy S21+',
                  'Galaxy S21 Ultra',
                  'Galaxy S22',
                  'Galaxy S22+',
                  'Galaxy S22 Ultra',
                  'Galaxy Note 20',
                  'Galaxy Note 20 Ultra 5G',
                  'Galaxy Fold',
                  'Galaxy Z Fold 2 5G',
                  'Galaxy Z Fold 3 5G',
                  'Galaxy Z Fold 4',
                  'Galaxy Z Flip',
                  'Galaxy Z Flip 3 5G',
                  'Galaxy Z Flip 4',
                ].map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-textSecondary">Not compatible:</p>
              <ul className="list-disc ml-5 mt-1 text-xs space-y-1">
                {[
                  'Galaxy S20 FE 4G/5G',
                  'Galaxy S20/S21 (US versions)',
                  'Galaxy Z Flip 5G (US versions)',
                  'Galaxy Note 20 Ultra (US and Hong Kong versions)',
                  'Galaxy Z Fold 2 (US and Hong Kong versions)',
                ].map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>

            {/* Google Pixel */}
            <div className="mt-4">
              <p className="font-medium text-sm">Google Pixel</p>
              <ul className="list-disc ml-5 mt-1 text-sm grid grid-cols-1 sm:grid-cols-2 gap-y-1">
                {[
                  'Pixel 2*',
                  'Pixel 2 XL',
                  'Pixel 3*',
                  'Pixel 3 XL',
                  'Pixel 3a*',
                  'Pixel 4',
                  'Pixel 4a',
                  'Pixel 4 XL',
                  'Pixel 5',
                  'Pixel 5a',
                  'Pixel 6',
                  'Pixel 6a',
                  'Pixel 6 Pro',
                  'Pixel 7',
                  'Pixel 7 Pro',
                ].map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
              <div className="mt-2 text-xs text-textSecondary space-y-1">
                <p>* Pixel 2: only phones bought with Google Fi service.</p>
                <p>
                  * Pixel 3: Australia/Japan/Taiwan models not compatible; US/CA carrier variants (except Sprint/Google
                  Fi) may not support eSIM.
                </p>
                <p>* Pixel 3a: South East Asia/Japan or Verizon variants may not support eSIM.</p>
              </div>
            </div>

            {/* Huawei */}
            <div className="mt-4">
              <p className="font-medium text-sm">Huawei</p>
              <ul className="list-disc ml-5 mt-1 text-sm">
                {['P40', 'P40 Pro', 'Mate 40 Pro'].map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-textSecondary">Not compatible: P40 Pro+, P50 Pro</p>
            </div>

            {/* Oppo */}
            <div className="mt-4">
              <p className="font-medium text-sm">Oppo</p>
              <ul className="list-disc ml-5 mt-1 text-sm">
                {['Find X3 Pro', 'Reno 5A', 'Find X5', 'Find X5 Pro'].map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-textSecondary">Not compatible: Find X5 Lite</p>
            </div>

            {/* Motorola & Others */}
            <div className="mt-4">
              <p className="font-medium text-sm">Motorola & Others</p>
              <ul className="list-disc ml-5 mt-1 text-sm grid grid-cols-1 sm:grid-cols-2 gap-y-1">
                {[
                  'Motorola Razr 2019',
                  'Motorola Razr 5G',
                  'Gemini PDA',
                  'Rakuten Mini',
                  'Rakuten Big-S',
                  'Rakuten Big',
                  'Rakuten Hand',
                  'Rakuten Hand 5G',
                  'Sony Xperia 10 III Lite',
                  'Sony Xperia 10 IV',
                  'Sony Xperia 1 IV',
                  'Surface Pro X',
                  'Honor Magic 4 Pro',
                  'Fairphone 4',
                  'Sharp Aquos Sense 6S',
                  'Sharp Aquos Wish',
                  'Xiaomi 12T Pro',
                  'DOOGEE V30',
                ].map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Laptops / Notebooks */}
          <div className="mt-4 rounded-lg border border-border p-4">
            <h3 className="font-medium">Laptop & Notebook Devices</h3>
            <ul className="list-disc ml-5 mt-2 text-sm grid grid-cols-1 sm:grid-cols-2 gap-y-1">
              {[
                'Microsoft Surface Pro X',
                'Microsoft Surface Go 2',
                'Microsoft Surface Pro LTE Advanced',
                'Acer Swift 3',
                'Acer Swift 7',
                'Asus Mini Transformer',
                'Asus NovaGo',
                'Asus VivoBook Flip 14',
                'HP Elitebook G5',
                'HP Probook G5',
                'HP Zbook G5',
                'HP Spectre Folio 13',
                'Lenovo Yoga C630',
                'Lenovo Miix 630',
                'Lenovo Yoga 520',
                'Samsung Galaxy Book 2',
              ].map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* FOOTER / HELP */}
        <footer className="text-xs text-textSecondary">Need help? Contact support@reloadpi.com</footer>
      </div>
    </div>
  );
}
